/**
 * Long-lived personal API tokens — the credential an MCP client keeps in its config.
 *
 * The token itself is opaque and never stored: only its SHA-256 hash reaches the database, and the
 * plaintext exists exactly once, in `CreatedApiToken.token`, in the response to its own creation.
 * Everything a client can read back afterwards is `ApiTokenSummary`.
 *
 * The token is not a session credential. It is exchanged for an ordinary short-lived access JWT —
 * the same one `/login` issues, with the same claims — so nothing below the gateway has to learn a
 * second identity format.
 */

export enum ApiTokenScope {
  MCP_READ = 'mcp:read',
}

/** Everything about a token that is safe to show its owner. Never carries the token or its hash. */
export interface ApiTokenSummary {
  id: number;
  name: string;
  scopes: ApiTokenScope[];
  /** Pinned when the token is created; it does not follow the user switching portals. */
  portalId: number;
  createdAt: string;
  expiresAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

export interface CreateApiTokenPayload {
  name: string;
  /** Defaults to `auth.apiToken.expiresDays`; capped at `auth.apiToken.maxExpiresDays`. */
  expiresInDays?: number;
  /** Defaults to `[ApiTokenScope.MCP_READ]`. */
  scopes?: ApiTokenScope[];
}

/**
 * The only shape carrying the plaintext token. Returned once, on creation, and never again —
 * a lost token is replaced, not recovered.
 */
export interface CreatedApiToken extends ApiTokenSummary {
  token: string;
}

export interface ExchangeApiTokenPayload {
  token: string;
}

export interface ExchangeApiTokenResult {
  accessToken: string;
  /**
   * The scopes of the token that was exchanged — not of the user behind it.
   *
   * They travel beside the JWT rather than inside it because the JWT is the ordinary access token,
   * issued by `/login` too, and it must stay identical whichever door it came through. The gateway
   * is the only consumer: it decides from these which MCP tools the call may reach, so a read-only
   * token cannot be handed a write tool no matter what its owner is allowed to do.
   */
  scopes: ApiTokenScope[];
}
